/**
 * Mock data for UI preview — enabled via ?mock=true or VITE_MOCK=true.
 * Realistic Threads analytics data for demonstrating all dashboard pages.
 */

import type {
  UserData,
  InsightsSummary,
  DailyData,
  PostsData,
  HeatmapData,
  HeatmapCell,
} from "./api";

// ── Helpers ──

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// ── User & Accounts ──

export const mockUser: UserData = {
  id: "usr_mock_001",
  username: "yarnalytics",
  name: "Yarnalytics Demo",
  profilePictureUrl: null,
  subscriptionStatus: "free",
  timezone: "Asia/Ho_Chi_Minh",
  createdAt: Math.floor(Date.now() / 1000) - 86400 * 60,
  accounts: [
    {
      id: "acc_mock_001",
      threadsUserId: "threads_001",
      username: "yarnalytics",
      name: "Yarnalytics Demo",
      profilePictureUrl: null,
      isPrimary: true,
      lastSyncedAt: Math.floor(Date.now() / 1000) - 1800,
      syncEnabled: true,
    },
    {
      id: "acc_mock_002",
      threadsUserId: "threads_002",
      username: "designstudio",
      name: "Design Studio",
      profilePictureUrl: null,
      isPrimary: false,
      lastSyncedAt: Math.floor(Date.now() / 1000) - 3600,
      syncEnabled: true,
    },
  ],
};

// ── Insights Summary ──

export function getMockSummary(period: string): InsightsSummary {
  const multiplier = period === "90d" ? 12 : period === "30d" ? 4 : 1;
  return {
    followersCount: 12_847,
    newFollowers: 342 * multiplier,
    views: 45_230 * multiplier,
    likes: 2_890 * multiplier,
    replies: 456 * multiplier,
    reposts: 178 * multiplier,
    quotes: 67 * multiplier,
    period,
    startDate: daysAgo(period === "90d" ? 90 : period === "30d" ? 30 : 7),
    endDate: daysAgo(0),
  };
}

// ── Daily Chart Data ──

const baseValues: Record<string, { base: number; variance: number }> = {
  views: { base: 6200, variance: 2000 },
  likes: { base: 380, variance: 120 },
  replies: { base: 60, variance: 25 },
  reposts: { base: 22, variance: 10 },
  quotes: { base: 8, variance: 5 },
  new_followers: { base: 45, variance: 20 },
};

export function getMockDaily(
  period: string,
  metric: string
): DailyData {
  const days = period === "90d" ? 90 : period === "30d" ? 30 : 7;
  const { base, variance } = baseValues[metric] || { base: 100, variance: 50 };

  // Generate data with a realistic upward trend + weekly cycles
  const metrics = Array.from({ length: days }, (_, i) => {
    const dayOfWeek = new Date(daysAgo(days - 1 - i) + "T00:00:00").getDay();
    // Weekday boost (Tue-Thu higher engagement)
    const weekdayMultiplier =
      dayOfWeek >= 2 && dayOfWeek <= 4 ? 1.3 : dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1.0;
    // Gradual growth trend
    const trendMultiplier = 1 + (i / days) * 0.15;
    const value = Math.round(
      (base + randomBetween(-variance, variance)) * weekdayMultiplier * trendMultiplier
    );
    return {
      date: daysAgo(days - 1 - i),
      value: Math.max(0, value),
    };
  });

  return {
    metrics,
    metric,
    period,
    startDate: daysAgo(days),
    endDate: daysAgo(0),
  };
}

// ── Posts ──

const samplePosts = [
  {
    text: "Just shipped a major update to our analytics dashboard. The new heatmap feature shows exactly when your audience is most active. Check it out!",
    mediaType: "TEXT_POST",
  },
  {
    text: "5 tips for growing your Threads audience organically:\n\n1. Post consistently\n2. Engage with replies\n3. Share valuable insights\n4. Use data to find best times\n5. Be authentic",
    mediaType: "TEXT_POST",
  },
  {
    text: "Our users grew 40% this month! Here's what we learned about building a product people actually want to use...",
    mediaType: "TEXT_POST",
  },
  {
    text: null,
    mediaType: "IMAGE",
  },
  {
    text: "The best time to post on Threads? It depends on YOUR audience. That's exactly why we built the engagement heatmap feature.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Replying to every comment on your posts isn't just nice — it's a growth strategy. Here's the data to prove it.",
    mediaType: "TEXT_POST",
  },
  {
    text: null,
    mediaType: "VIDEO",
  },
  {
    text: "Monday motivation: Your analytics from last week show progress. Small wins compound into big results.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Unpopular opinion: Follower count matters less than engagement rate. Here's why quality > quantity for creators.",
    mediaType: "TEXT_POST",
  },
  {
    text: "New feature drop: Daily trend charts now show 90-day views for Pro users. Track your growth over time with beautiful monochrome charts.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Asked 50 creators what tool they wish existed for Threads. The #1 answer? Post-level analytics. We built exactly that.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Data-driven content strategy:\n\n- Post when your heatmap shows peak activity\n- Double down on post types with highest engagement\n- Track followers daily, not weekly\n\nSimple framework, powerful results.",
    mediaType: "TEXT_POST",
  },
  {
    text: null,
    mediaType: "CAROUSEL",
  },
  {
    text: "Threads is still early. The creators who invest in understanding their data now will have a massive advantage later.",
    mediaType: "TEXT_POST",
  },
  {
    text: "We just crossed 1,000 connected accounts! Thank you to every creator who trusts us with their analytics.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Quick tip: Your top-performing posts usually have one thing in common — authenticity. Data shows real stories get 3x more engagement.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Building in public update: Phase 5 of Yarnalytics is complete. Full web dashboard with Threads-native design.",
    mediaType: "TEXT_POST",
  },
  {
    text: null,
    mediaType: "IMAGE",
  },
  {
    text: "The algorithm rewards consistency. Here's proof: accounts posting daily saw 2.4x more views than those posting weekly.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Weekend analytics tend to dip, but Sunday evenings are a hidden gem for posting. Check your heatmap!",
    mediaType: "TEXT_POST",
  },
  {
    text: "Free vs Pro: even our free tier gives you powerful overview analytics. Pro unlocks post-level data and the engagement heatmap.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Your reposts are your best content ideas. Look at what resonates and create more of it.",
    mediaType: "TEXT_POST",
  },
  {
    text: "We analyzed 10,000 Threads posts. The ideal length? 80-150 characters. Short, punchy, and valuable.",
    mediaType: "TEXT_POST",
  },
  {
    text: "Sometimes the best analytics insight is the simplest one: just show up every day.",
    mediaType: "TEXT_POST",
  },
];

export function getMockPosts(
  period: string,
  sort: string,
  page: number
): PostsData {
  const now = Math.floor(Date.now() / 1000);
  const limit = 20;

  // Generate all posts
  const allPosts = samplePosts.map((p, i) => ({
    id: `post_mock_${String(i + 1).padStart(3, "0")}`,
    text: p.text,
    mediaType: p.mediaType,
    permalink: `https://www.threads.net/@yarnalytics/post/${String.fromCharCode(65 + i)}${randomBetween(100, 999)}`,
    publishedAt: now - randomBetween(3600, 86400 * 7) - i * 3600,
    views: randomBetween(800, 25000),
    likes: randomBetween(30, 1200),
    replies: randomBetween(5, 180),
    reposts: randomBetween(2, 85),
    quotes: randomBetween(0, 30),
    shares: randomBetween(1, 45),
  }));

  // Sort
  switch (sort) {
    case "views":
      allPosts.sort((a, b) => b.views - a.views);
      break;
    case "likes":
      allPosts.sort((a, b) => b.likes - a.likes);
      break;
    case "replies":
      allPosts.sort((a, b) => b.replies - a.replies);
      break;
    default: // recent
      allPosts.sort((a, b) => b.publishedAt - a.publishedAt);
  }

  // Paginate
  const start = (page - 1) * limit;
  const posts = allPosts.slice(start, start + limit);

  return {
    posts,
    total: allPosts.length,
    page,
    limit,
    period,
  };
}

// ── Heatmap ──

export function getMockHeatmap(period: string): HeatmapData {
  const days = period === "90d" ? 90 : period === "30d" ? 30 : 7;
  const cells: HeatmapCell[] = [];

  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      // Realistic engagement patterns:
      // - Higher during business hours (9-18)
      // - Peak at lunch (12-13) and evening (19-21)
      // - Low overnight (0-6)
      // - Weekdays > weekends
      let intensity = 0;

      // Time-of-day pattern
      if (hour >= 0 && hour < 6) {
        intensity = randomBetween(0, 3);
      } else if (hour >= 6 && hour < 9) {
        intensity = randomBetween(5, 20);
      } else if (hour >= 9 && hour < 12) {
        intensity = randomBetween(15, 45);
      } else if (hour >= 12 && hour < 14) {
        intensity = randomBetween(30, 65); // Lunch peak
      } else if (hour >= 14 && hour < 17) {
        intensity = randomBetween(20, 50);
      } else if (hour >= 17 && hour < 19) {
        intensity = randomBetween(25, 55);
      } else if (hour >= 19 && hour < 22) {
        intensity = randomBetween(35, 70); // Evening peak
      } else {
        intensity = randomBetween(5, 15);
      }

      // Day-of-week modifier
      const isWeekend = day === 0 || day === 6;
      if (isWeekend) {
        intensity = Math.round(intensity * 0.6);
      } else if (day >= 2 && day <= 4) {
        // Tue-Thu boost
        intensity = Math.round(intensity * 1.2);
      }

      // Scale by period
      const periodMultiplier = days / 7;
      const likes = Math.round(intensity * 0.7 * periodMultiplier);
      const replies = Math.round(intensity * 0.3 * periodMultiplier);

      cells.push({
        day,
        hour,
        likes,
        replies,
        total: likes + replies,
      });
    }
  }

  return {
    cells,
    period,
    startDate: daysAgo(days),
    endDate: daysAgo(0),
  };
}

// ── Billing ──

export const mockBillingCheckout = {
  paddlePriceId: "pri_mock_pro_monthly",
  paddleClientToken: "test_mock_client_token",
  paddleCustomerId: null,
  userId: "usr_mock_001",
  paddleEnvironment: "sandbox",
};

export const mockBillingPortal = {
  url: "#mock-paddle-portal",
};
